// Step 1 — read and sign the customer's documents. RON-5465.
// Prototype regions: `.doc-item`, `.scroll-reader`, `.sign-block`.
//
// THE SCROLL GATE IS THE POINT OF THIS SCREEN. §3 returns `read_to_end` per document, and where it
// is true the signature is withheld until the reader has actually been scrolled to the bottom.
// That is not theatre: the customer is relying on this record if somebody is hurt on their site,
// and a signature collected without the text having been on screen is worth nothing to them.
//
// A document that was signed and then REISSUED gets its own words, and the mechanism is worth
// stating precisely because we described it wrongly at first.
//
// IT IS NOT A MID-PACK CHANGE. §5.4 forbids that: "a pack must stay readable years later even
// after the document is revised" — an admin publishing v4.0 does not rewrite the text under
// somebody halfway through signing v2.4.
//
// It is ACROSS packs. The contractor signed v2.4 in an earlier pack; the customer published v4.0;
// a new pack was created and carries v4.0. The spec says exactly this — "they get version 2 the
// next time a pack is created, which is a decision, not a side effect of an edit."
//
// So in THIS pack they have signed the document, just an older version of it. "Not signed" would
// tell them they missed something they did not miss, which is why the prototype ships this case
// as its own (`<!-- Doc 3: Needs re-acknowledgement -->`) with its own badge.

import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ACK_METHOD,
  DOC_STATE,
  formatLongDate,
  formatShortDate,
} from "../portalConstants.js";

// How close to the bottom counts as "read to the end". A few pixels of slack, because a scroll
// container rarely lands exactly on its own height — sub-pixel rounding and elastic scrolling both
// leave a remainder, and a gate nobody can satisfy is worse than no gate.
const BOTTOM_SLACK_PX = 8;

const DocumentReader = ({ atEnd, body, onReachedEnd, progress, onProgress }) => {
  const { t } = useTranslation("common");
  const frame = useRef(0);

  const onScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const reachable = Math.max(1, scrollHeight - clientHeight);
      const ratio = Math.min(1, scrollTop / reachable);

      // Throttled to one update per frame. A scroll handler that sets state on every event will
      // fire dozens of times per flick on a phone, which is exactly the device this is read on.
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        onProgress(Math.round(ratio * 100));

        if (scrollTop + clientHeight >= scrollHeight - BOTTOM_SLACK_PX) onReachedEnd();
      });
    },
    [onProgress, onReachedEnd]
  );

  return (
    <div className="portal-reader">
      <div className="portal-reader__body" onScroll={onScroll}>
        {body.map((paragraph, index) =>
          // A short line with no sentence end is a heading in these documents.
          paragraph.length < 60 && !paragraph.endsWith(".") ? (
            <h4 key={index}>{paragraph}</h4>
          ) : (
            <p key={index}>{paragraph}</p>
          )
        )}
      </div>

      {/* LABEL then BAR on the left, the hint on the right — the prototype's order. We had the
          label on the left and the bar-plus-percentage on the right, which split one piece of
          information across the width of the card and left the hint nowhere. */}
      <div className="portal-reader__foot">
        <span className="portal-reader__progress">
          <span className="portal-reader__label">
            {atEnd
              ? t("react_portal_read_complete")
              : progress > 0
                ? t("react_portal_read_pct").replace("%n", String(progress))
                : t("react_portal_scroll_to_read")}
          </span>
          <span className="portal-reader__bar">
            <span className="portal-reader__fill" style={{ width: `${progress}%` }} />
          </span>
        </span>

        {/* Gone once the document has been read, as in the prototype: it is an instruction, and
            an instruction that has been followed is clutter. */}
        {!atEnd && (
          <span className="portal-reader__hint">{t("react_portal_scroll_first")}</span>
        )}
      </div>
    </div>
  );
};

const DocumentRow = ({
  doc,
  body,
  isBusy,
  isActive = false,
  error = null,
  notice = null,
  isLocked = false,
  onOpen,
  onSign,
}) => {
  const { t } = useTranslation("common");

  const [reachedEnd, setReachedEnd] = useState(!doc.readToEnd);
  const [progress, setProgress] = useState(0);
  const [consented, setConsented] = useState(false);
  const [typedName, setTypedName] = useState("");

  const isSigned = doc.state === DOC_STATE.SIGNED;

  // This row's own write, as opposed to somebody else's holding the page. Every control is
  // disabled while any write runs, so without the `isActive` half each button would claim to be
  // the one signing.
  const signing = isActive && isBusy;
  const needsName = doc.ackMethod === ACK_METHOD.ESIGN;
  // `isLocked` wins over everything else: a submitted pack locks signatures for good (Phase C),
  // and the server refuses the press anyway. Listed first so the reason is obvious when reading.
  const canSign =
    !isLocked &&
    reachedEnd &&
    consented &&
    !isBusy &&
    (!needsName || typedName.trim().length > 1);

  const meta = [
    doc.description,
    doc.version && t("react_portal_version").replace("%s", doc.version),
    doc.updatedAt && t("react_portal_updated").replace("%s", formatLongDate(doc.updatedAt)),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="portal-doc">
      <div className="portal-doc__header">
        <span
          className={`portal-doc__status portal-doc__status--${isSigned ? "ok" : "bad"}`}
          aria-hidden="true"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {isSigned ? (
              <polyline points="20 6 9 17 4 12" />
            ) : (
              <>
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </>
            )}
          </svg>
        </span>

        <div className="portal-doc__info">
          <p className="portal-doc__title">{doc.title}</p>
          <p className="portal-doc__meta">{meta}</p>

          {isSigned ? (
            <span className="portal-doc__badge portal-doc__badge--ok">
              {t("react_portal_signed_on").replace("%s", formatLongDate(doc.signedAt?.slice(0, 10)))}
            </span>
          ) : (
            // The reissued case gets its own words. "To sign" would suggest they had simply not
            // got round to it, when in fact they did and the document changed afterwards.
            <span
              className={`portal-doc__badge portal-doc__badge--${doc.reissued ? "warn" : "bad"}`}
            >
              {doc.reissued && doc.signedVersion
                ? t("react_portal_resign_required_versions")
                    .replace("%s", String(doc.signedVersion))
                    .replace("%c", String(doc.version ?? ""))
                : t(
                    doc.reissued
                      ? "react_portal_resign_required"
                      : "react_portal_state_awaiting_sign"
                  )}
            </span>
          )}
        </div>

        {isSigned && (
          <span className="portal-doc__done">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {t("react_portal_done")}
          </span>
        )}
      </div>

      {isSigned && (
        <p className="portal-doc__signed" role="status">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {/* Three facts, in the prototype's order: WHO signed, WHEN, and WHICH VERSION. The
              version is the one that matters on a reissued document — "signed" alone does not
              say whether it was this text or the one before it.

              `signedVersion` is the server's own field and is preferred over `version`, which
              is the version on screen now and can already have moved on. */}
          {t("react_portal_signed_by")
            .replace("%n", doc.signedName || "")
            .replace("%d", formatShortDate(String(doc.signedAt || "").slice(0, 10)))
            .replace("%v", String(doc.signedVersion ?? doc.version ?? ""))}
        </p>
      )}

      {/* ERRORS ONLY, as of 27-October-2026. A successful signature is already announced by the
          row above, which is what the prototype shows and says more than "Signed." does — who,
          when, which version. Printing both put a second green box under the first. */}
      {isActive && !isBusy && error && (
        <p className="portal-upload__msg portal-upload__msg--bad" role="alert">
          {error}
        </p>
      )}

      {!isSigned && (
        <>
          {body ? (
            <DocumentReader
              atEnd={reachedEnd}
              body={body}
              progress={progress}
              onProgress={setProgress}
              onReachedEnd={() => setReachedEnd(true)}
            />
          ) : (
            <button type="button" className="portal-upload" onClick={() => onOpen(doc)}>
              <p className="portal-upload__title">{t("react_portal_open_document")}</p>
              <p className="portal-upload__sub">{t("react_portal_open_document_sub")}</p>
            </button>
          )}

          {/* HIDDEN UNTIL READ, as in the prototype — 26-October-2026. It used to be drawn from
              the start with every control disabled, which offers somebody a form and then refuses
              it; the prototype reveals it as the reward for finishing the document.

              `reachedEnd` starts TRUE for a document whose `read_to_end` is false, so an ungated
              document still shows its block immediately. The gate is the server's, not ours.

              The `disabled={!reachedEnd}` props inside are now always false, and are kept
              deliberately: they are the gate that actually decides, and this wrapper is only
              presentation. If the block is ever revealed earlier, they still hold. */}
          {reachedEnd && (
            <div className="portal-sign">
              <label className="portal-sign__consent">
              <input
                type="checkbox"
                checked={consented}
                // Unavailable until the text has been on screen. The gate is on the checkbox
                // rather than only on the button, so the reason is where the first click lands.
                disabled={!reachedEnd}
                onChange={(event) => setConsented(event.target.checked)}
              />
              <span className="portal-sign__box" aria-hidden="true">
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              {t("react_portal_consent")}
            </label>

            {needsName && (
              <div className="portal-sign__row">
                <input
                  type="text"
                  className="portal-sign__input"
                  value={typedName}
                  onChange={(event) => setTypedName(event.target.value)}
                  placeholder={t("react_portal_type_name")}
                  aria-label={t("react_portal_type_name")}
                  disabled={!reachedEnd}
                />
                <button
                  type="button"
                  className="portal-sign__btn"
                  disabled={!canSign || signing}
                  // All three facts §5 asks for, from the controls that observed them. `reachedEnd`
                  // starts true where the document does not gate on reading, which is the same
                  // answer the server expects for one whose `read_to_end` is false.
                  onClick={() =>
                    onSign(doc, { name: typedName, consent: consented, readToEnd: reachedEnd })
                  }
                >
                  {signing ? t("react_portal_signing") : t("react_portal_sign")}
                </button>
              </div>
            )}

            {!needsName && (
              <div className="portal-sign__row">
                <button
                  type="button"
                  className="portal-sign__btn"
                  disabled={!canSign || signing}
                  // A tick-only document still records a name — §5: "the row always says who" —
                  // so the server fills in the contractor's own. We send none rather than invent.
                  onClick={() => onSign(doc, { consent: consented, readToEnd: reachedEnd })}
                >
                  {signing ? t("react_portal_signing") : t("react_portal_sign")}
                </button>
              </div>
            )}

              <p className="portal-sign__note">
                {t("react_portal_sign_note").replace("%s", doc.version || "")}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// `isLocked` — Phase C. A submitted pack locks signatures and the personnel tick FOR GOOD; only
// documents stay replaceable. Without it the sign button stayed live on a submitted pack and the
// server refused the press with "This pack has been submitted and can no longer be changed."
//
// In practice nothing is outstanding at submit time, so this was a latent gap rather than a live
// bug — but a button that cannot work should not be pressable.
const PortalReadSign = ({
  documents,
  bodies,
  isBusy,
  isLocked = false,
  onOpen,
  onSign,
  // The row a write is running on, and what to say about it when it finishes.
  activeDocId = null,
  error = null,
  notice = null,
}) => {
  const { t } = useTranslation("common");

  return (
    <>
      <p className="portal-section__intro">{t("react_portal_read_intro")}</p>

      {documents.map((doc) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          body={bodies[doc.id]}
          isBusy={isBusy}
          isActive={doc.id === activeDocId}
          error={error}
          notice={notice}
          isLocked={isLocked}
          onOpen={onOpen}
          onSign={onSign}
        />
      ))}
    </>
  );
};

export default PortalReadSign;
